import React, { useMemo, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { NewTextField, Paper, Grid, NewSelect, Button, Typography } from '../../common';
import history from '../../history';
import FormGroup from '@material-ui/core/FormGroup';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Services from '../../service-utils/services';
import { useQuery, useMutation } from 'react-query';
import queryString from 'query-string';
import { handleMsgOnForm } from '../../common/utils';
import { CACHE_TIME, STALE_TIME } from './../../common/constants';
import { useSnackbar } from 'notistack';
import { isEmpty } from 'lodash';
import getSessionData from '../../service-utils/session-util';
import { orderBy } from 'lodash';
import AddIcon from '@material-ui/icons/Add';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setHeader } from '../../libs/redux-sdk/actions';
//import { ConformationBox } from '../../components/conformationBox';
import { CONTENT_URL } from './../../common/constants';
import PageSkeleton from './pageSkeleton';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import OverlapLoader from '../../common/loader/OverlapLoader';

const CREATIVES = {
  banner: 'Banner',
  video: 'Video',
};

const BANNER_TYPES = [
  {
    value: 2,
    label: 'Banner adv',
  },
  {
    value: 4,
    label: 'HTML 5 Adv',
  },
];
const VIDEO_TYPES = [
  {
    value: 'UPLOADED',
    label: 'Uploaded',
  },
  {
    value: 'VAST',
    label: 'Vast',
  },
];

const FILE_TYPE = {
  banner: 'Image/png, Image/jpeg, Image/jpg',
  html5: 'zip',
};

const CREATIVE_TYPES = Object.keys(CREATIVES).map((key) => ({
  id: key,
  name: CREATIVES[key],
}));

export const CreativesCreate = () => {
  const { creativeId } = useParams();
  const dispatch = useDispatch();
  const { agencyId, advertiserId } = getSessionData();
  const { enqueueSnackbar } = useSnackbar();
  const [bannerSizeOptions, setBannerSizeOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [attributesOptions, setAttributesOptions] = useState([]);
  const [mimeOptions, setMimeOptions] = useState([]);
  const [isSecure, setIsSecure] = useState(true);
  const [imageUrl, setImageUrl] = useState();
  //const [conformation, setConformation] = useState();
  const [selectedCreative, setSelectedCreative] = useState(CREATIVE_TYPES[0].id);
  const [checked, setChecked] = useState({});
  const [isPublishedValue, setIsPublishedValue] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [whatsAppNo, setWhatsappNo] = useState('');
  const [whatsAppMsg, setWhatsappMsg] = useState('');
  const [isWhatsapp, setWhatsapp] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState('');

  const constructWhatsAppUrl = (whatsAppNo, whatsAppMsg) => {
    const encodedWhatsAppMsg = encodeURIComponent(whatsAppMsg);
    return `https://api.whatsapp.com/send/?phone=${whatsAppNo}&text=${encodedWhatsAppMsg}`;
  };

  useEffect(() => {
    if (isWhatsapp) {
      const url = constructWhatsAppUrl(whatsAppNo, whatsAppMsg);
      setWhatsappUrl(url);
    } else {
      setWhatsappUrl(''); // Reset the URL when isWhatsapp is false
    }
  }, [isWhatsapp, whatsAppNo, whatsAppMsg]);

  useEffect(() => {
    dispatch(
      setHeader({
        back: {
          text: 'Back',
          link: '/advertiser/creative', // Replace with your desired path
        },
      }),
    );
  }, [dispatch]);

  const { data: allAdvertiser = [] } = useQuery(
    ['ALL_ADVERTISERS', agencyId],
    async () => {
      const payload = {
        pageSize: 5000,
        pageNum: 0,
        enabled: true,
        agencyId,
      };
      const response = await Services.advertisersGetAll(queryString.stringify(payload));
      return response.data?.advertiserList;
    },
    { enabled: !!agencyId },
  );

  const { data: bannerSizes = [] } = useQuery(
    ['MASTER_BANNER_SIZES', agencyId],
    async () => {
      const payload = { agencyId };
      const response = await Services.getMasterBannerSizes(queryString.stringify(payload));
      return response.data?.masterList;
    },
    { enabled: !!agencyId, staleTime: STALE_TIME.HALF_HOUR, cacheTime: CACHE_TIME.HALF_HOUR },
  );

  const { data: languages = {} } = useQuery(
    ['MASTER_LANGUAGE', agencyId],
    async () => {
      const payload = { agencyId };
      const response = await Services.getMasterLanguages(queryString.stringify(payload));
      return response.data?.masterList;
    },
    { enabled: !!agencyId, staleTime: STALE_TIME.HALF_HOUR, cacheTime: CACHE_TIME.HALF_HOUR },
  );

  const { data: mimeTypes = {} } = useQuery(
    ['MASTER_MIME_TYPES', agencyId],
    async () => {
      const payload = { agencyId };
      const response = await Services.getMasterMimeTypes(queryString.stringify(payload));
      return response.data?.masterList;
    },
    { enabled: !!agencyId, staleTime: STALE_TIME.HALF_HOUR, cacheTime: CACHE_TIME.HALF_HOUR },
  );

  const { data: attrCheckboxOptions = {} } = useQuery(
    ['MASTER_CREATIVE_ATTRIBUTES', agencyId],
    async () => {
      const payload = { agencyId };
      const response = await Services.getMasterCreativeAttr(queryString.stringify(payload));
      return response.data?.masterList;
    },
    { enabled: !!agencyId, staleTime: STALE_TIME.HALF_HOUR, cacheTime: CACHE_TIME.HALF_HOUR },
  );

  const { data: creativeData = {} } = useQuery(
    ['CREATIVE_DATA', creativeId, agencyId, advertiserId],
    async () => {
      const payload = { agencyId };
      const response = await Services.creativeGetDetails(
        advertiserId,
        creativeId,
        queryString.stringify(payload),
      );
      return response.data?.creativeDTO;
    },
    { enabled: !!creativeId && !!agencyId },
  );

  //creativeCreate
  const { mutate: creativeCreate, isLoading: isLoadingCreativeCreate } = useMutation(
    async (payload) => {
      const query = queryString.stringify({ agencyId });
      const response = await Services.creativeCreate(advertiserId, query, payload);
      return response.data;
    },
    {
      onError: (err) => {
        handleMsgOnForm(err, enqueueSnackbar, setServerErrors);
      },
      onSuccess: (data, payload) => {
        //uploadImage(data?.creativeDTO?.id);
        enqueueSnackbar('Creative updated successfully.', {
          variant: 'success',
        });
        history.push(`/advertiser/creative-image/${data?.creativeDTO?.id}`);
      },
    },
  );

  //creativeUpdate
  const { mutate: creativeUpdate, isLoading: isLoadingCreativeUpdate } = useMutation(
    async (payload) => {
      const query = queryString.stringify({ agencyId });
      const response = await Services.creativeUpdate(advertiserId, creativeId, query, payload);
      return response.data;
    },
    {
      onError: (err) => {
        handleMsgOnForm(err, enqueueSnackbar, setServerErrors);
      },
      onSuccess: (data, payload) => {
        //uploadImage(data?.creativeDTO?.id);
        enqueueSnackbar('Creative updated successfully.', {
          variant: 'success',
        });
        history.push(`/advertiser/creative-image/${data?.creativeDTO?.id}`);
      },
    },
  );

  const handleOnSubmit = (values, isPublished = false) => {
    if (isPublishedValue && creativeId) {
      history.push(`/advertiser/creative-image/${creativeId}`);
    } else {
      let payload = {
        advertiserId: values.advertiser.value,
        name: values.creativeName,
        masterCreativeAttributes: Object.keys(checked)
          .filter((key) => checked[key])
          .map((i) => parseInt(i)),
        published: isPublished,
      };
      if (creativeData.id) {
        payload = {
          ...payload,
          id: creativeData.id,
        };
      }
      if (CREATIVES[selectedCreative] === CREATIVES.banner) {
        payload = {
          ...payload,
          bannerDTO: {
            adType: values.bannerType?.value,
            bannerSizeId: values.banner?.value,
            externalImpressionTrackerUrl: values.externalImpressionTrackerUrl,
            landingPageUrl: values.landingPageUrl || whatsappUrl,
            languageId: values.language?.value,
            secure: isSecure,
          },
        };
      } else {
        payload = {
          ...payload,
          videoDTO: {
            duration: 0,
            landingPageUrl: values.landingPageUrl || whatsappUrl,
            thumbnailUrl: values.thumbnailUrl,
            uploadedVideoUrl: values.uploadedVideoUrl,
            languageId: values.language?.value,
            mimeTypes: values.mimeTypes?.map((item) => item.value) || [],
            secure: isSecure,
            vastTag: values.vastTag,
            vastVersion: values.vastVersion,
            vastWrapper: values.vastWrapper,
            videoType: values.videoType?.value,
          },
        };
      }
      !!creativeId ? creativeUpdate(payload) : creativeCreate(payload);
    }
  };

  const { handleSubmit, values, touched, errors, setFieldValue } = useFormik({
    initialValues: {
      creativeName: '',
      advertiser: {},
      banner: {},
      language: {},
      mimeTypes: [],
      bannerType: BANNER_TYPES[0],
      externalImpressionTrackerUrl: '',
      landingPageUrl: '',
      thumbnailUrl: '',
      uploadedVideoUrl: '',
      vastTag: '',
      vastVersion: '',
      vastWrapper: '',
      videoType: {},
    },
    validationSchema: Yup.object({
      creativeName: Yup.string().max(255).required('Name is required.'),
      // landingPageUrl: Yup.string().max(255),
      externalImpressionTrackerUrl: Yup.string().max(255),
    }),
    onSubmit: () => handleOnSubmit(values),
  });

  useEffect(() => {
    if (!isEmpty(bannerSizes)) {
      const data = bannerSizes.map((item) => ({
        ...item,
        value: item.id,
      }));
      setBannerSizeOptions(data);
      setFieldValue('banner', data[0]);
    }
  }, [bannerSizes, setFieldValue]);

  useEffect(() => {
    if (!isEmpty(languages)) {
      const data = languages.map((item) => ({
        ...item,
        value: item.id,
      }));
      setLanguageOptions(data);
      setFieldValue('language', data[0]);
    }
  }, [languages, setFieldValue]);

  useEffect(() => {
    if (!isEmpty(mimeTypes)) {
      const data = mimeTypes.map((item) => ({
        ...item,
        value: item.id,
      }));
      setMimeOptions(data);
      setFieldValue('mimeTypes', data[0]);
    }
  }, [mimeTypes, setFieldValue]);

  useEffect(() => {
    if (!isEmpty(attrCheckboxOptions)) {
      const data = attrCheckboxOptions.map((item) => ({
        ...item,
        value: item.id,
        name: item.label,
      }));
      setAttributesOptions(data);
    }
  }, [attrCheckboxOptions]);

  const allAdvertisersOptions = useMemo(() => {
    return (
      orderBy(
        allAdvertiser?.map((item) => ({
          ...item,
          label: item.name,
          value: item.id,
          disabled: item.status,
        })),
        [(advertiser) => advertiser.label.toLowerCase()],
        ['asc'],
      ) || []
    );
  }, [allAdvertiser]);

  const handleCheckboxChange = (event, id) => {
    setChecked((state) => ({ ...state, [id]: event.target.checked }));
  };

  useEffect(() => {
    const {
      name,
      advertiserId,
      published,
      masterCreativeAttributes = [],
      bannerDTO,
      videoDTO,
    } = creativeData || {};
    if (!isEmpty(creativeData)) {
      name && setFieldValue('creativeName', name);
      published && setIsPublishedValue(published);
      advertiserId &&
        setFieldValue(
          'advertiser',
          allAdvertisersOptions.find((item) => item.value === advertiserId),
        );

      if (!isEmpty(masterCreativeAttributes) && !isEmpty(attrCheckboxOptions)) {
        let checkedData = {};
        for (var i = 0; i < masterCreativeAttributes.length; i++) {
          checkedData = { ...checkedData, [masterCreativeAttributes[i]]: true };
        }
        setChecked(checkedData);
      }

      if (!isEmpty(bannerDTO)) {
        setSelectedCreative('banner');
        setFieldValue(
          'bannerType',
          BANNER_TYPES.find((item) => item.value === bannerDTO.adType),
        );
        bannerDTO.secure && setIsSecure(bannerDTO.secure);
        bannerDTO.externalImpressionTrackerUrl &&
          setFieldValue('externalImpressionTrackerUrl', bannerDTO.externalImpressionTrackerUrl);
        if (isWhatsapp) {
          setFieldValue('landingPageUrl', whatsappUrl);
        } else {
          bannerDTO.landingPageUrl && setFieldValue('landingPageUrl', bannerDTO.landingPageUrl);
        }
        // bannerDTO.landingPageUrl && setFieldValue('landingPageUrl', bannerDTO.landingPageUrl);
        bannerDTO.languageId &&
          setFieldValue(
            'language',
            languageOptions.find((item) => item.value === bannerDTO.languageId),
          );
        bannerDTO.bannerSizeId &&
          setFieldValue(
            'banner',
            bannerSizeOptions.find((item) => item.value === bannerDTO.bannerSizeId),
          );
        bannerDTO.bannerUrl && setImageUrl(bannerDTO.bannerUrl);
      } else if (!isEmpty(videoDTO)) {
        setSelectedCreative('video');
      }
    }
  }, [
    creativeData,
    setFieldValue,
    languageOptions,
    bannerSizeOptions,
    attrCheckboxOptions,
    allAdvertisersOptions,
    isWhatsapp,
    whatsappUrl,
  ]);

  return (
    <div className="col-md-10 bg-white rounded shadow m-auto mb-4 p-0">
      <Paper className="pt-3 p-4 mn-h-200">
        <div className="d-flex justify-content-center mb-3">
          <Typography color="textPrimary" variant="h5">
            {!!creativeId ? 'Edit Creative' : 'Create New Creative'}
          </Typography>
        </div>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} className="py-0">
              <RadioGroup
                name="singleSelect"
                value={selectedCreative?.toString()}
                onChange={(event) => setSelectedCreative(event.target.value)}
                className="d-flex flex-row"
              >
                {CREATIVE_TYPES.map((item) => (
                  <FormControlLabel
                    value={item.id?.toString()}
                    control={<Radio />}
                    label={item.name}
                    className="radio-btns"
                    disabled={CREATIVES[item.id] === CREATIVES.video}
                  />
                ))}
              </RadioGroup>
            </Grid>
            {/* <Grid item xs={12} sm={6} className="py-0">
              <NewSelect
                required
                options={allAdvertisersOptions}
                value={values.advertiser}
                onChange={(obj) => setFieldValue('advertiser', obj)}
                placeholder={`Advertiser Name`}
                className="py-2"
                disabled={isPublishedValue && creativeId}
                isOptionDisabled={() => isPublishedValue && creativeId}
              />
              {serverErrors.advertiser && (
                <p className="text-danger mt-n2">{serverErrors.advertiser}</p>
              )}
            </Grid> */}
            <Grid item xs={6} className="py-0 mb-1">
              <NewTextField
                required
                fullWidth
                info="Enter name for creative. e.g 728*90 banner, Mobile leaderboard etc"
                label="Name"
                margin="normal"
                name="creativeName"
                variant="outlined"
                error={Boolean(touched.creativeName && errors.creativeName)}
                helperText={touched.creativeName && errors.creativeName}
                value={values.creativeName}
                onChange={(event) => setFieldValue('creativeName', event.target.value)}
                disabled={isPublishedValue && creativeId}
              />{' '}
              {serverErrors.name && <p className="text-danger mt-n2">{serverErrors.name}</p>}
            </Grid>

            {/* <Grid item xs={12} sm={6} className="py-0 mt-2">
              <Button
                variant="contained"
                className="btn btn-primary d-flex btn-add"
                startIcon={<AddIcon />}
                onClick={() => history.push(`/advertiser-create`)}
                disabled={isPublishedValue && creativeId}
              >
                New Advertiser
              </Button>
            </Grid> */}

            <Grid item xs={6} className="py-0 mb-1">
              <NewSelect
                required
                info="Select which type of banner you are uploading static banner or html5 ad."
                options={BANNER_TYPES}
                value={values.bannerType}
                onChange={(obj) => setFieldValue('bannerType', obj)}
                placeholder={`Banner type`}
                className="py-2"
                disabled={creativeId}
                isOptionDisabled={() => creativeId}
              />{' '}
              {serverErrors.bannerType && (
                <p className="text-danger mt-n2">{serverErrors.bannerType}</p>
              )}
            </Grid>

            <Grid item xs={12} className="py-0 m-3">
              <FormGroup className="row flex-row">
                {attributesOptions.map((item) => (
                  <FormControlLabel
                    key={item.id}
                    className="col-4 mb-2 mr-0 p-0"
                    control={
                      <Checkbox
                        checked={checked[item.id]}
                        onChange={(e) => handleCheckboxChange(e, item.id)}
                        name={item.name}
                      />
                    }
                    label={item.name}
                    disabled={isPublishedValue && creativeId}
                  />
                ))}
              </FormGroup>
            </Grid>

            {CREATIVES[selectedCreative] === CREATIVES.banner && (
              <div className="creative-section border p-3 m-2">
                <div className="creative-title">{CREATIVES.banner}</div>
                <Grid className="row">
                  <Grid item xs={12} sm={6} className="py-0">
                    <NewSelect
                      info="Select appropriate size of your banner from available list"
                      options={bannerSizeOptions}
                      value={values.banner}
                      onChange={(obj) => setFieldValue('banner', obj)}
                      placeholder={`Banner size`}
                      className="py-2"
                      disabled={isPublishedValue && creativeId}
                      isOptionDisabled={() => isPublishedValue && creativeId}
                    />{' '}
                    {serverErrors.banner && (
                      <p className="text-danger mt-n2">{serverErrors.banner}</p>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} className="py-0">
                    <NewSelect
                      info="Select language of your banner"
                      options={languageOptions}
                      value={values.language}
                      onChange={(obj) => setFieldValue('language', obj)}
                      placeholder={`Language`}
                      className="py-2"
                      disabled={isPublishedValue && creativeId}
                      isOptionDisabled={() => isPublishedValue && creativeId}
                    />{' '}
                    {serverErrors.language && (
                      <p className="text-danger mt-n2">{serverErrors.language}</p>
                    )}
                  </Grid>
                </Grid>

                <Grid item xs={12} className="py-0">
                  <NewTextField
                    fullWidth
                    // label={
                    //   <div>
                    //     External Impression Tracker Url
                    //     <InfoOutlinedIcon
                    //       style={{ fontSize: 18, color: '#A9A9A9', marginLeft: 5 }}
                    //     />
                    //   </div>
                    // }
                    label="External Impression Tracker Url"
                    info="Enter URL of external impression tracker to verify the impressions received on adzylytics platform"
                    margin="normal"
                    name="externalImpressionTrackerUrl"
                    variant="outlined"
                    error={Boolean(
                      touched.externalImpressionTrackerUrl && errors.externalImpressionTrackerUrl,
                    )}
                    helperText={
                      touched.externalImpressionTrackerUrl && errors.externalImpressionTrackerUrl
                    }
                    value={values.externalImpressionTrackerUrl}
                    onChange={(event) => {
                      setFieldValue('externalImpressionTrackerUrl', event.target.value);
                    }}
                    disabled={
                      (isPublishedValue && creativeId) ||
                      values.bannerType?.value === BANNER_TYPES[1].value
                    }
                  />{' '}
                  {serverErrors.externalTrackerUrl && (
                    <p className="text-danger mt-n2">{serverErrors.externalTrackerUrl}</p>
                  )}
                </Grid>
                {/* <div>
                  <FormControlLabel
                    key={'whatsapp'}
                    control={
                      <Checkbox
                        checked={isWhatsapp}
                        onChange={(e) => setWhatsapp(e.target.checked)}
                        name={'whatsapp'}
                        disabled={isPublishedValue && creativeId}
                      />
                    }
                    label={
                      <div>
                        <img
                          src="data:image/jpeg;base64,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"
                          alt="WhatsApp Icon"
                          style={{ height: '25px' }}
                        />
                        {' WhatsApp Landing'}
                      </div>
                    }
                    disabled={isPublishedValue && creativeId}
                  />

                  {isWhatsapp ? (
                    <>
                      {/* Render these fields when isWhatsapp is true */}
                {/* <Grid item xs={12} className="py-0">
                        <NewTextField
                          fullWidth
                          required
                          label="WhatsApp number"
                          margin="normal"
                          name="whatsappnumber"
                          variant="outlined"
                          onChange={(event) => {
                            setWhatsappNo(event.target.value);
                          }}
                          disabled={isPublishedValue && creativeId}
                          inputProps={{
                            inputMode: 'numeric', // Allow only numeric input
                            pattern: '[0-9]*', // Pattern to enforce numeric input (optional but can be used for browsers that support it)
                            onInput: (event) => {
                              event.target.value = event.target.value.replace(/\D/g, ''); // Remove non-numeric characters
                            },
                          }}
                        />
                      </Grid> */}
                {/* <Grid item xs={12} className="py-0">
                        <NewTextField
                          fullWidth
                          // required
                          label="WhatsApp Message"
                          margin="normal"
                          name="whatsappmessage"
                          onChange={(event) => {
                            setWhatsappMsg(event.target.value);
                          }}
                          variant="outlined"
                          disabled={isPublishedValue && creativeId}
                        />
                      </Grid>
                      <Grid item xs={12} className="py-0">
                        <NewTextField
                          fullWidth
                          required
                          label="Landing Page Url"
                          margin="normal"
                          name="landingPageUrl"
                          variant="outlined"
                          error={Boolean(touched.landingPageUrl && errors.landingPageUrl)}
                          helperText={touched.landingPageUrl && errors.landingPageUrl}
                          value={
                            values.landingPageUrl === null && values.landingPageUrl === undefined
                              ? values.landingPageUrl
                              : whatsappUrl
                          }
                          onChange={(event) => {
                            setFieldValue('landingPageUrl', event.target.value);
                          }}
                          disabled={isPublishedValue && creativeId}
                          // disabled={true}
                          readOnly={true}
                        />
                      </Grid>
                    </>
                  ) : (
                    // Render this field when isWhatsapp is false
                    <Grid item xs={12} className="py-0">
                      <NewTextField
                        fullWidth
                        required
                        label="Landing Page Url"
                        margin="normal"
                        name="landingPageUrl"
                        variant="outlined"
                        error={Boolean(touched.landingPageUrl && errors.landingPageUrl)}
                        helperText={touched.landingPageUrl && errors.landingPageUrl}
                        value={values.landingPageUrl}
                        onChange={(event) => {
                          setFieldValue('landingPageUrl', event.target.value);
                        }}
                        disabled={isPublishedValue && creativeId}
                      />
                      {serverErrors.landingPageUrl && (
                        <p className="text-danger mt-n2">{serverErrors.landingPageUrl}</p>
                      )}
                    </Grid>
                  )}
                </div> */}
                <Grid item xs={12} className="py-0">
                  <NewTextField
                    fullWidth
                    required
                    info="Enter Url or link of your landing page. e.g Website link, Opt-in Page, Forms, etc"
                    label="Landing Page Url"
                    margin="normal"
                    name="landingPageUrl"
                    variant="outlined"
                    error={Boolean(touched.landingPageUrl && errors.landingPageUrl)}
                    helperText={touched.landingPageUrl && errors.landingPageUrl}
                    value={values.landingPageUrl}
                    onChange={(event) => {
                      setFieldValue('landingPageUrl', event.target.value);
                    }}
                    disabled={isPublishedValue && creativeId}
                  />
                  {serverErrors.landingPageUrl && (
                    <p className="text-danger mt-n2">{serverErrors.landingPageUrl}</p>
                  )}
                </Grid>
                <Grid item xs={12} className="py-0 mt-2">
                  <FormControlLabel
                    key={'secure'}
                    control={
                      <Checkbox
                        checked={isSecure}
                        onChange={(e) => setIsSecure(e.target.checked)}
                        name={'secure'}
                      />
                    }
                    label={'Is Secure'}
                    disabled={isPublishedValue && creativeId}
                  />
                </Grid>
                {/*values.bannerType?.value === BANNER_TYPES[0].value && imageUrl && (
                  <Grid className="row">
                    <Grid item xs={6} className="py-0 mt-2">
                      {creativeData?.bannerDTO?.adType === 4 ? (
                        <iframe src={`${CONTENT_URL}/${imageUrl}`} title="creative" />
                      ) : (
                        <img
                          className="mw-100 border rounded-sm "
                          src={`${CONTENT_URL}/${imageUrl}`}
                          alt="banner"
                        />
                      )}
                    </Grid>
                    <Grid item xs={6} className="py-0 mt-2">
                      <div className="pt-0">
                        <Button
                          variant="contained"
                          type="submit"
                          onClick={() => {
                            history.push(`/advertiser/creative-image/${creativeId}`);
                          }}
                          className="btn btn-primary d-flex mt-2"
                          disabled={isPublishedValue}
                        >
                          Replace Image
                        </Button>
                        <Button
                          variant="contained"
                          className="btn btn-primary d-flex mt-2"
                          onClick={() => {
                            setIsPreviewVisible(true);
                          }}
                        >
                          Preview
                        </Button>
                      </div>
                    </Grid>
                  </Grid>
                )*/}
              </div>
            )}
          </Grid>
          <div className="mt-4 d-flex justify-content-end ">
            <Button
              variant="contained"
              className="btn btn-secondary d-flex"
              onClick={() => history.push(`/advertiser/creative?advertiserId=${advertiserId}`)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              className="btn btn-primary d-flex ml-3"
              disabled={isLoadingCreativeCreate || isLoadingCreativeUpdate}
            >
              {!creativeId ? 'Proceed' : 'Proceed'}
            </Button>
            {/*creativeId && (
              <Button
                variant="contained"
                type="submit"
                className="btn btn-primary d-flex ml-3"
                disabled={(isPublishedValue && creativeId) || isEmpty(imageUrl)}
                onClick={(e) => {
                  e.preventDefault();
                  setConformation(true);
                }}
              >
                Update And Publish
              </Button>
            )*/}
          </div>
          {(isLoadingCreativeCreate || isLoadingCreativeUpdate) && <OverlapLoader />}
        </form>
      </Paper>

      {isPreviewVisible && (
        <PageSkeleton
          bannerSize={bannerSizeOptions.find(
            (item) => item.value === creativeData?.bannerDTO?.bannerSizeId,
          )}
          imageUrl={`${CONTENT_URL}/${imageUrl}`}
          handleClose={() => {
            setIsPreviewVisible(false);
          }}
        />
      )}
      {/*<ConformationBox
        isOpen={!!conformation}
        handleClose={() => setConformation()}
        handleSubmit={() => handleOnSubmit(values, true)}
        title="confirm ?"
        subtitle="Are you sure you want to Publish this creative? Once publish it cannot be rollback."
        btnCloseLabel="Close"
        btnSubmitLabel="confirm"
      />*/}
    </div>
  );
};
